import React, { useEffect, useState } from "react";
import createAxiosInstance from "../../utils/axiosConfig";
import '../../assets/scss/sidebar.css';
import Alert from './alerts/alert';

interface UploadLogoSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const UploadLogoSidebar: React.FC<UploadLogoSidebarProps> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<any>(null);
  const [users, setUsers] = useState<any>([]);
  const [selectedUserId, setSelectedUserId] = useState<any>(null);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const processFile = (selectedFile: any) => {
    if (!selectedFile) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const maxWidth = 1920;
    const maxHeight = 1080;
    const maxResolution = 300;

    if (selectedFile.size > maxSize) {
      setErrors({ file: "File size exceeds the maximum limit of 5MB." });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const resolution = selectedFile.size / (width * height);

        if (width > maxWidth || height > maxHeight) {
          setErrors({
            file: `Image dimensions should not exceed ${maxWidth}x${maxHeight} pixels.`,
          });
          return;
        }

        if (resolution > maxResolution) {
          setErrors({
            file: `Image resolution should not exceed ${maxResolution} DPI.`,
          });
          return;
        }

        setFile(selectedFile);
        setErrors({});
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files[0];
    processFile(selectedFile);
  };



  const handleUserSelect = (userId: string, userEmail: string) => {
    setSelectedUserId(userId);
    setSearchTerm(userEmail);
    setIsDropdownOpen(false);
  };

  const calculateDropdownPosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    });
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const element = e.currentTarget;
    calculateDropdownPosition(element);
    setIsDropdownOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear selection if user starts typing again
    if (selectedUserId && value !== users?.results?.find((user: { id: any; }) => user.id === selectedUserId)?.email) {
      setSelectedUserId(null);
    }
    
    // Show dropdown when user types
    if (value.length > 0) {
      const element = e.currentTarget;
      calculateDropdownPosition(element);
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  // Debounce search term for API calls (to avoid too many requests)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // Faster debounce for better UX

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users when component mounts or search term changes
  useEffect(() => {
    if (isOpen) {
      fetchUsers(debouncedSearchTerm);
    }
  }, [isOpen, debouncedSearchTerm]);

  // Handle clicking outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchInput = document.querySelector(".user-search-input");
      const dropdownElement = event.target as Element;
      
      // Check if click is outside both the search input and any dropdown content
      if (searchInput && !searchInput.contains(event.target as Node)) {
        // Check if the click is inside any dropdown content
        const isInsideDropdown = dropdownElement.closest('[style*="position: fixed"]');
        
        if (!isInsideDropdown) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const fetchUsers = async (searchQuery: string) => {
    try {
      const axiosInstance = createAxiosInstance();
      const endpoint = searchQuery ? `/users?email=${searchQuery}` : '/users';
      const response = await axiosInstance.get(endpoint, {
        params: {
          offset: 0,
          limit: 500,
        },
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrors({ file: "Please select a file before submit." });
      return;
    }

    if (!selectedUserId) {
      setErrors({ user_id: "Please select a user." });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", selectedUserId);

    try {
      const axiosInstance = createAxiosInstance({
        "Content-Type": "multipart/form-data",
      });

      const response = await axiosInstance.post(
        `/upload/logo?user_id=${selectedUserId}`,
        formData
      );
      
      // Only show success and reset form if response is successful and has filename and location
      if (response.data && response.data.filename && response.data.location) {
        setAlert({
          message: "File uploaded successfully!",
          type: 'success',
          show: true
        });
        setLoading(false);
        setFile(null);
        setSelectedUserId(null);
        setErrors({});
        setSearchTerm("");
      } else {
        setAlert({
          message: "Upload failed: Invalid response from server.",
          type: 'danger',
          show: true
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setAlert({
        message: "Error uploading file. Try again later.",
        type: 'danger',
        show: true
      });
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const onButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg';
    input.onchange = handleFileChange;
    input.click();
  };

  if (!isOpen) return null;



  return (
    <div>
      <div
        className="offcanvas-backdrop fade show"
        onClick={onClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '500px',
          maxWidth: '90vw',
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <div className="d-flex align-items-center">
            <div 
              className="me-4"
              style={{
                cursor: 'pointer',
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#183FCE',
                padding: '10px 15px',
                borderRadius: '8px',
                border: '0px solid #183FCE'
              }}
            >
              <i className="bi bi-images me-2"></i>
              UPLOAD LOGO
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        
        <div className="offcanvas-body p-4" style={{ overflowY: 'auto', overflowX: 'visible' }}>
          {alert && alert.show && (
            <Alert
              message={alert.message}
              type={alert.type}
              show={alert.show}
              onClose={() => setAlert(null)}
            />
          )}



          <div className="mb-4">
            
            <div className="position-relative mb-3">
              <div className="">
                <label className="form-label" style={{ fontSize: '14px', fontWeight: 'bold', color: '#183FCE' }}>Search User</label>
                <input
                  type="text"
                  className="form-control bg-white user-search-input"
                  placeholder="Type to search for a user..."
                  value={searchTerm}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  style={{ minHeight: '56px' }}
                />
                
              </div>
              
              {selectedUserId && (
                <div className="mt-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                  <span className="small text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Selected: {users?.results?.find((user: { id: any; }) => user.id === selectedUserId)?.email}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSelectedUserId(null);
                      setSearchTerm("");
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
              
              {errors.user_id && (
                <div className="text-danger small">{errors.user_id}</div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <h6 className="mb-3" style={{ fontWeight: 'bold', color: '#183FCE' }}>
              Upload Logo
            </h6>
            
            {/* Drag and Drop Area */}
            <div
              className={`upload-drop-zone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={onButtonClick}
              style={{ minHeight: '200px' }}
            >
              <i className="bi bi-cloud-upload" style={{ fontSize: '3rem', color: '#183FCE', marginBottom: '1rem' }}></i>
              <h6 className="mb-2">Drag and Drop or Click to Upload</h6>
              <p className="text-muted small mb-0">
                Upload a transparent PNG, JPG, or JPEG file
              </p>
              <p className="text-muted small">
                Max file size: 5MB | Max dimensions: 1920×1080 pixels
              </p>
            </div>

            {file && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>Selected file:</strong> {file.name}
                    <br />
                    <small className="text-muted">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </small>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setFile(null)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {errors.file && (
              <div className="text-danger small mt-2">{errors.file}</div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={loading || !file || !selectedUserId}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  Upload Logo
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Working dropdown using direct fixed positioning */}
      {isDropdownOpen && searchTerm.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            maxHeight: '300px',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.175)',
            zIndex: 1080,
            overflow: 'hidden',
            fontSize: '1rem'
          }}
          onClick={(e) => e.stopPropagation()}
        >
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {(() => {
              const filteredUsers = users?.results?.filter((user: any) => 
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
              ) || [];

              if (filteredUsers.length === 0) {
                return (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    <i className="bi bi-search"></i>
                    <p className="mb-0 mt-2">No users found matching "{searchTerm}"</p>
                  </div>
                );
              }

              return (
                <>
                  {filteredUsers.length > 5 && (
                    <div style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee', fontSize: '0.875rem', color: '#666' }}>
                      {filteredUsers.length} users found - showing all results
                    </div>
                  )}
                  {filteredUsers.map((user: any) => (
                <div
                  key={`dropdown_${user.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUserSelect(user.id, user.email);
                  }}
                  style={{ 
                    cursor: 'pointer', 
                    padding: '10px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div style={{ fontWeight: '500' }}>{user.email}</div>
                  {user.name && (
                                         <div style={{ fontSize: '0.875rem', color: '#666' }}>{user.name}</div>
                   )}
                 </div>
                 ))}
                </>
               );
             })()}
           </div>
        </div>
      )}
      

    </div>
  );
};

export default UploadLogoSidebar; 